/**
 * CRMContextBanner — shows the context pill switcher in the CRM top bar.
 *
 * Place this inside each Dashboard's topbar section.
 * Clicking a pill navigates to the correct route for that context.
 *
 * Context → Route mapping:
 *   self  → /crm-self  (employee-level own data view for admin/manager)
 *   team  → /manager
 *   admin → /admin
 *   exec  → /executive
 */
import React from "react";
import { redirectWithSsoCode } from "../utils/ssoRedirect";
import { useNavigate, useLocation } from "react-router-dom";
import { useRoleContext } from "../hooks/useRoleContext";

// Route for each context (what the pill click navigates to)
const CTX_ROUTE = {
  self:  "/crm-self",
  team:  "/manager",
  admin: "/admin",
  exec:  "/executive",
};

const APP_URLS = {
  hrms:    process.env.REACT_APP_HRMS_URL    || "http://localhost:5175",
  crm:     window.location.origin,
  expense: process.env.REACT_APP_EXPENSE_URL || "http://localhost:3002",
};

const APP_META = [
  { key: "hrms",    icon: "👤", label: "HRMS"    },
  { key: "crm",     icon: "📊", label: "CRM"     },
  { key: "expense", icon: "💰", label: "Expense" },
];

export default function CRMContextBanner({ user, inSidebar = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { context, setContext, pillDef, allowedApps } = useRoleContext(user, "crm");

  if (!pillDef.labels.length) return null; // employee / exec: no pills needed

  const handlePill = (ctx) => {
    setContext(ctx);
    navigate(CTX_ROUTE[ctx] || "/dashboard");
  };

  const accent = user?.company === "ebekmed" ? "#2AB5A5" : "#E31E24";

  // ── Sidebar (vertical) mode — used inside the employee dark rail ──
  if (inSidebar) {
    return (
      <div style={{ padding: "0 4px 12px" }}>
        <div style={{ fontSize: 9, fontWeight: 800, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6, paddingLeft: 4 }}>
          Viewing as
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {pillDef.labels.map((label, i) => {
            const ctx = pillDef.ctxs[i];
            const active = context === ctx;
            return (
              <button key={ctx} onClick={() => handlePill(ctx)}
                style={{
                  padding: "8px 12px", border: "1px solid",
                  borderColor: active ? "transparent" : "rgba(255,255,255,0.15)",
                  borderRadius: 12,
                  background: active ? `linear-gradient(135deg, ${accent}, ${accent}cc)` : "rgba(255,255,255,0.06)",
                  color: active ? "#fff" : "rgba(255,255,255,0.65)",
                  fontSize: 12, fontWeight: 800, fontFamily: "inherit",
                  cursor: "pointer", textAlign: "left",
                  boxShadow: active ? `0 4px 12px ${accent}44` : "none",
                }}>
                {label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: "#f8fafc",
      borderBottom: "1px solid #e2e8f0",
      padding: "8px 20px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
    }}>
      {/* Pill switcher */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 10, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginRight: 4 }}>
          Viewing as
        </span>
        {pillDef.labels.map((label, i) => {
          const ctx = pillDef.ctxs[i];
          const active = context === ctx;
          return (
            <button
              key={ctx}
              onClick={() => handlePill(ctx)}
              style={{
                padding: "6px 16px",
                border: "1.5px solid",
                borderColor: active ? "transparent" : "#e2e8f0",
                borderRadius: 20,
                background: active ? `linear-gradient(135deg, ${accent}, ${accent}cc)` : "#ffffff",
                color: active ? "#ffffff" : "#64748b",
                fontSize: 12,
                fontWeight: 800,
                fontFamily: "inherit",
                cursor: "pointer",
                transition: "all 0.15s",
                boxShadow: active ? `0 3px 10px ${accent}44` : "none",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* App switcher (right side) */}
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 10, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginRight: 2 }}>
          Apps
        </span>
        {APP_META.map(({ key, icon, label }) => {
          const locked = !allowedApps.includes(key);
          const isCur  = key === "crm";
          return (
            <button
              key={key}
              title={locked ? `${label} — access restricted` : label}
              disabled={locked}
              onClick={async () => {
                if (locked || isCur) return;
                const hrmsToken = localStorage.getItem("hrms_token");
                if (key === "hrms") {
                  if (!hrmsToken) { window.location.href = APP_URLS.hrms; return; }
                  await redirectWithSsoCode({ targetUrl: APP_URLS.hrms, token: hrmsToken });
                  return;
                }
                if (key === "expense") {
                  try {
                    await redirectWithSsoCode({ targetUrl: APP_URLS.expense, token: hrmsToken });
                  } catch { window.location.href = APP_URLS.expense; }
                }
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                padding: "5px 10px",
                border: "1.5px solid",
                borderColor: isCur ? accent : locked ? "#e2e8f0" : "#e2e8f0",
                borderRadius: 8,
                background: isCur ? `${accent}18` : "#ffffff",
                color: isCur ? accent : locked ? "#cbd5e1" : "#64748b",
                fontSize: 11,
                fontWeight: 800,
                fontFamily: "inherit",
                cursor: locked ? "not-allowed" : isCur ? "default" : "pointer",
                opacity: locked ? 0.4 : 1,
              }}
            >
              <span style={{ fontSize: 14 }}>{icon}</span>
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
